import React, { useState, useCallback, useEffect } from "react";
import useFormCache from "../hooks/useFormCache";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  FormControl,
  FormLabel,
  Checkbox,
  Select,
  MenuItem,
  ListItemText,
  OutlinedInput,
  TextField,
  Button,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Collapse,
  List,
  ListItem,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { ShowCustomAlert } from "./showCustomAlert";
import { 
  BACKENDS, 
  MODULES, 
  VEHICLES, 
  BRANDS, 
  MARKETS, 
  MAIL_TYPES, 
  MailType, 
  STATUS_OPTIONS,
  canApplyUpdateTag,
  formatStatusWithUpdate,
  SERVICES,
  ServiceCategory,
  ServicesShort
} from "../configs/breakingNewsConfig";
import CENPreview from "./CENPreview";
import BNPreview from "./BNPreview";
import { sendEmail } from "../apis/SendEmailsApi"; // Import your sendEmail function
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useAuth } from "../middleware/AuthContext";


// ---------------------------------------------
// Form data interface
// ---------------------------------------------
interface CENBNFormData extends Record<string, unknown> {
  mailTypes: MailType[];
  status: string;
  cenStatus: string;
  bnStatus: string;
  isStatusUpdate: boolean;
  affectedBackends: string[];
  affectedModules: string[];
  affectedVehicles: string[];
  affectedServices: string[];
  outageType: string;
  affectedBrands: string[];
  affectedMarkets: string[];
  impactedCustomerServices: string[];
  leadIncidentId: string;
  startDisturbance: string;
  endDisturbance: string;
  customerImpactText: string;
  technicalDescription: string;
  additionalInformation: string;
  gmtOffset: string;
  rootCause: string;
  fix: string;
}

// ---------------------------------------------
// Component
// ---------------------------------------------
const AnnouncementForm: React.FC<{ finalDecision?: string | null }> = ({ finalDecision: _finalDecision }) => {
  const { csrfToken } = useAuth(); // Get CSRF token from Auth context
  const [
    formData,
    setFormData,
    ,
    cacheControls,
  ] = useFormCache<CENBNFormData>(
    "cen-bn-form-cache",
    {
      mailTypes: [],
      status: "",
      cenStatus: "",
      bnStatus: "",
      isStatusUpdate: false,
      affectedBackends: [],
      affectedModules: [],
      affectedVehicles: [],
      affectedServices: [],
      outageType: "",
      affectedBrands: [],
      affectedMarkets: [],
      impactedCustomerServices: [],
      leadIncidentId: "",
      startDisturbance: "",
      endDisturbance: "",
      customerImpactText: "",
      technicalDescription: "",
      additionalInformation: "",
      gmtOffset: "",
      rootCause: "",
      fix: "",
    },
    ["gmtOffset"] // gmtOffset is computed dynamically – don't persist it
  );

  const [showCachePrompt, setShowCachePrompt] = useState(cacheControls.hasCachedData);

  useEffect(() => {
    // --- PDE Service Exception Logic for Form State ---
    const pdeBrands = ["Seat&Cupra", "All brands"];
    const pdeTriggerKeywords = [
      "Enrollment", "MSG", "IDK", "VUM", "GroupRetailPortal", "MessageHub"
    ];

    const shouldAddPDE =
      formData.affectedBrands.some((brand: string) => pdeBrands.includes(brand)) &&
      (
        formData.affectedModules.concat(formData.affectedServices)
          .some((item: string) =>
            pdeTriggerKeywords.some((keyword: string) =>
              item.toLowerCase().includes(keyword.toLowerCase())
            )
          )
      );

    // Only add if not already present
    if (
      shouldAddPDE &&
      !formData.affectedServices.includes("PDE (PreDelivery Enrollment)")
    ) {
      setFormData(prev => ({
        ...prev,
        affectedServices: [...prev.affectedServices, "PDE (PreDelivery Enrollment)"]
      }));
    }

    // Optionally, remove PDE if rule no longer matches
    if (
      !shouldAddPDE &&
      formData.affectedServices.includes("PDE (PreDelivery Enrollment)")
    ) {
      setFormData(prev => ({
        ...prev,
        affectedServices: prev.affectedServices.filter(
          (s: string) => s !== "PDE (PreDelivery Enrollment)"
        )
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.affectedBrands,
    formData.affectedModules,
    formData.affectedServices
  ]);

  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // Ensure loading state is defined
  const [berlinTime, setBerlinTime] = useState("");
  const [gmtOffset, setGmtOffset] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [modulesSearchTerm, setModulesSearchTerm] = useState("");
  const [servicesSearchTerm, setServicesSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  // State for additional affected items block
  const [showAdditionalAffected, setShowAdditionalAffected] = useState(false);

  // ---------- Time ----------
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const berlinTimeFormatted = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Berlin",
      }).format(now);

      const withTimeZone = now.toLocaleString("en-US", {
        timeZone: "Europe/Berlin",
        timeZoneName: "short",
      });

      const match = withTimeZone.match(/GMT([+-]\d+)/);
      const offset = match ? `GMT${match[1]}` : "undefined";

      setBerlinTime(berlinTimeFormatted);
      setGmtOffset(offset);
      setFormData((prev) => ({ ...prev, gmtOffset: offset }));
    };

    updateTime();
    const interval = setInterval(updateTime, 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = (status: string) => {
    // Check if both mail types are selected and status is "Change"
    if (
      status === "Change" &&
      formData.mailTypes.includes("Breaking News") &&
      !formData.mailTypes.includes("Critical Event Notification")
    ) {
      ShowCustomAlert("'Change' status cannot be used for Breaking News announcements.");
      return;
    }

    setActiveStatus(status);
    
    // Update status based on selected mail types
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        status,
        isStatusUpdate: canApplyUpdateTag(status) ? prev.isStatusUpdate : false,
      };
      
      // Update the appropriate status based on mail types
      if (prev.mailTypes.includes("Critical Event Notification") || prev.mailTypes.includes("Test Mail")) {
        updatedFormData.cenStatus = status;
      }
      
      // Update BN status for all cases where Breaking News is selected
      // Only apply non-"Change" statuses to BN
      if (prev.mailTypes.includes("Breaking News")) {
        updatedFormData.bnStatus = status === "Change" ? prev.bnStatus : status;
      }
      
      return updatedFormData;
    });
  };

  // Effect to update active status when mail types change
  useEffect(() => {
    if (formData.mailTypes.includes("Critical Event Notification")|| formData.mailTypes.includes("Test Mail")) {
      // When CEN is selected, show its status
      setActiveStatus(formData.cenStatus || null);
      setFormData(prev => ({ ...prev, status: prev.cenStatus }));
    } else if (formData.mailTypes.includes("Breaking News")) {
      // When only BN is selected, show its status
      setActiveStatus(formData.bnStatus || null);
      setFormData(prev => ({ ...prev, status: prev.bnStatus }));
    } else {
      // Reset when no mail type is selected
      setActiveStatus(null);
      setFormData(prev => ({ ...prev, status: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bnStatus, formData.cenStatus, formData.mailTypes]);

  useEffect(() => {
    if (!canApplyUpdateTag(formData.status) && formData.isStatusUpdate) {
      setFormData((prev) => ({ ...prev, isStatusUpdate: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.status, formData.isStatusUpdate]);

  const getEffectiveStatus = useCallback(
    (status: string) => formatStatusWithUpdate(status, formData.isStatusUpdate),
    [formData.isStatusUpdate]
  );

  // ---------- Handlers ----------
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = e.target as HTMLInputElement & { name: keyof typeof formData; value: unknown };
      setFormData((prev) => ({ ...prev, [target.name]: target.value }));
    },
    [setFormData]
  );

  const handleArraySelect = (name: keyof typeof formData) => (event: SelectChangeEvent<string[]>) => {
    // Get the selected values from the event
    const selectedValues = event.target.value as string[];
    
    // Special handling for brands
    if (name === "affectedBrands") {
      // Check if "All brands" is being selected/deselected
      const allBrandsSelected = selectedValues.includes("All brands");
      const wasAllBrandsSelected = formData.affectedBrands.includes("All brands");
      
      // If "All brands" is newly selected, remove all other selections
      if (allBrandsSelected && !wasAllBrandsSelected) {
        setFormData((prev) => ({ ...prev, [name]: ["All brands"] }));
        return;
      }
      
      // If other brands are selected while "All brands" is already selected, remove "All brands"
      if (wasAllBrandsSelected && selectedValues.length > 1 && allBrandsSelected) {
        const filteredValues = selectedValues.filter(brand => brand !== "All brands");
        setFormData((prev) => ({ ...prev, [name]: filteredValues }));
        return;
      }
    }
    
    // Default behavior for other fields or when special cases don't apply
    setFormData((prev) => ({ ...prev, [name]: selectedValues }));
  };

const validateBNBrands = useCallback(() => {
  // List of allowed brands
  const allowedBrands = ["All brands", "All brands (except Ford)", "Seat&Cupra", "Volkswagen", "Skoda", "Ford"];
  
  // Check if any of the allowed brands are selected
  return formData.affectedBrands.some(brand => allowedBrands.includes(brand));
}, [formData.affectedBrands]);

const validateForm = React.useCallback(() => {
  const missing: string[] = [];
  if (formData.mailTypes.length === 0) missing.push("Mail Type (CEN/BN)");
  if (!formData.status) missing.push("Status");
  if (formData.affectedVehicles.length === 0) missing.push("Affected Generation(s)");
  if (!formData.outageType) missing.push("Type of Outage");
  if (formData.affectedBrands.length === 0) missing.push("Affected Brand(s)");
  if (formData.affectedMarkets.length === 0) missing.push("Affected Market(s)");
  if (formData.impactedCustomerServices.length === 0) missing.push("Affected Impacted Service(s)");
  if (!formData.leadIncidentId) missing.push("Lead Incident ID");
  if (!formData.startDisturbance) missing.push("Start of Disturbance");
  if (!formData.customerImpactText) missing.push("Customer Impact");
  if (
    (formData.status === "Resolved" || formData.cenStatus === "Resolved" || formData.bnStatus === "Resolved")
    && !formData.rootCause
  ) missing.push("Root Cause (Root cause description block)");
  if (
    (formData.status === "Resolved" || formData.cenStatus === "Resolved" || formData.bnStatus === "Resolved")
    && !formData.fix
  ) missing.push("Fix (Root cause description block)");

  if (missing.length) {
    ShowCustomAlert(
      "Please fill out the following required fields:\n\n" +
        missing.map((m) => `- ${m}`).join("\n")
    );
    return false;
  }

  // Validate that end time is not before start time
  if (formData.endDisturbance && formData.startDisturbance) {
    const parseDateTime = (dateTimeStr: string) => {
      const [datePart, timePart] = dateTimeStr.split(' ');
      const [day, month, year] = datePart.split('.');
      const [hours, minutes] = timePart.split(':');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
    };
    
    const startDate = parseDateTime(formData.startDisturbance);
    const endDate = parseDateTime(formData.endDisturbance);
    
    if (endDate < startDate) {
      ShowCustomAlert("End of Disturbance cannot be earlier than Start of Disturbance");
      return false;
    }
  }

  if (
    formData.mailTypes.includes("Breaking News") && 
    formData.affectedBrands.length > 0 && 
    !validateBNBrands()
  ) {
    ShowCustomAlert(
      "Breaking News announcements can only be used with 'All brands' or when Seat/Cupra, Volkswagen, Skoda, or Ford are selected."
    );
    return false;
  }
  return true;
}, [formData.mailTypes, formData.status, formData.affectedVehicles.length, formData.outageType, formData.affectedBrands.length, formData.affectedMarkets.length, formData.impactedCustomerServices.length, formData.leadIncidentId, formData.startDisturbance, formData.customerImpactText, formData.cenStatus, formData.bnStatus, formData.rootCause, formData.fix, formData.endDisturbance, validateBNBrands]);

// 🔹 Auto-hide preview if new mail type is added but validation fails
useEffect(() => {
  if (showPreview && !validateForm()) {
    setShowPreview(false);
  }
}, [formData.mailTypes, showPreview, validateForm]); 

// Update the getFilteredBNBrands function to return uppercase brand names
const getFilteredBNBrands = () => {
  // If "All brands" is selected, return specific brands in uppercase
  if (formData.affectedBrands.includes("All brands")) {
    return ["Seat&Cupra", "Volkswagen", "Skoda", "Ford"].map(brand => brand.toUpperCase());
  }
  
  // If "All brands (except Ford)" is selected, return specific brands without Ford in uppercase
  if (formData.affectedBrands.includes("All brands (except Ford)")) {
    return ["Seat&Cupra", "Volkswagen", "Skoda"].map(brand => brand.toUpperCase());
  }
  
  // Otherwise filter to only include allowed brands and convert to uppercase
  const allowedBrands = ["All brands", "All brands (except Ford)", "Seat&Cupra", "Volkswagen", "Skoda", "Ford"];
  return formData.affectedBrands
    .filter(brand => allowedBrands.includes(brand))
    .map(brand => brand.toUpperCase());
};

const getAdditionalBrandLists = () => {
  const lists: string[] = [];
  if (formData.affectedBrands.includes("Ford")) {
    lists.push("FORD");
  }
  return lists;
};

// Add this effect to check brand selection when Breaking News is selected
useEffect(() => {
  // Only run validation if Breaking News is selected and there are brands selected
  if (
    formData.mailTypes.includes("Breaking News") && 
    formData.affectedBrands.length > 0 &&
    !validateBNBrands()
  ) {
    ShowCustomAlert(
      "Breaking News announcements can only be used with 'All brands' or when Seat/Cupra, VW, Skoda, or Ford are selected."
    );
  }
}, [formData.mailTypes, formData.affectedBrands, validateBNBrands]);  


  // Add this handler function for clearing selections
  const handleClearSelection = (fieldName: keyof typeof formData) => () => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: []
    }));
  };

  // Add this handler function to clear all affected items at once
  const handleClearAllSelections = () => {
    setFormData(prev => ({
      ...prev,
      affectedBackends: [],
      affectedModules: [],
      affectedVehicles: [],
      affectedBrands: [],
      affectedMarkets: [],
      affectedServices: [], 
      impactedCustomerServices: [], 
    }));
    setModulesSearchTerm(''); // Also clear the modules search term
    setServicesSearchTerm(''); // Also clear the services search term
  };

  // Filter services based on search term
  const filteredServices = servicesSearchTerm 
    ? SERVICES.filter(category => 
        category.parent.toLowerCase().includes(servicesSearchTerm.toLowerCase()) || 
        category.children.some(child => 
          child.toLowerCase().includes(servicesSearchTerm.toLowerCase())
        )
      )
    : SERVICES;
  
  function toggleExpandCategory(parent: string): void {
    setExpandedCategories(prev => 
      prev.includes(parent) 
        ? prev.filter(cat => cat !== parent)
        : [...prev, parent]
    );
  }

  function isCategoryFullySelected(category: ServiceCategory): boolean {
    return category.children.every(child => 
      formData.affectedServices.includes(child)
    );
  }

  function isCategoryPartiallySelected(category: ServiceCategory): boolean {
    return category.children.some(child => 
      formData.affectedServices.includes(child)
    ) && !isCategoryFullySelected(category);
  }

  function handleToggleCategory(category: ServiceCategory) {
    const isCurrentlyFullySelected = isCategoryFullySelected(category);
    
    if (isCurrentlyFullySelected) {
      // Deselect all children
      setFormData(prev => ({
        ...prev,
        affectedServices: prev.affectedServices.filter(
          service => !category.children.includes(service)
        )
      }));
    } else {
      // Select all children
      const newServices = [...formData.affectedServices];
      
      category.children.forEach(child => {
        if (!newServices.includes(child)) {
          newServices.push(child);
        }
      });
      
      setFormData(prev => ({
        ...prev,
        affectedServices: newServices
      }));
    }
  }

  function isServiceSelected(child: string): boolean {
    return formData.affectedServices.includes(child);
  }

  function handleToggleService(child: string): void {
    if (isServiceSelected(child)) {
      // Remove the service
      setFormData(prev => ({
        ...prev,
        affectedServices: prev.affectedServices.filter(s => s !== child)
      }));
    } else {
      // Add the service
      setFormData(prev => ({
        ...prev,
        affectedServices: [...prev.affectedServices, child]
      }));
    }
  }

  // ---------- UI ----------
  return (
    <Box sx={{ margin: "auto" }}>
      {/* Cache Prompt */}
      {showCachePrompt && (
        <Box sx={{ bgcolor: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 2, p: 2, mb: 3 }}>
          <Typography sx={{ mb: 2, fontWeight: "bold" }}>
            Found previously saved form data (cached within last 12 hours)
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                cacheControls.loadCache();
                setShowCachePrompt(false);
              }}
            >
              Load Saved Data
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                cacheControls.skipCache();
                setShowCachePrompt(false);
              }}
            >
              Start Fresh
            </Button>
          </Box>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
          Breaking News / Critical Event Notification
        </Typography>

        {/* Mail Types */}
        <FormControl component="fieldset" sx={{ display: "block", mb: 2 }}>
          <FormLabel component="legend">
            Mail Type(s) <span style={{ color: "red" }}>*</span>
          </FormLabel>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {MAIL_TYPES.map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    name="mailTypes"
                    value={type}
                    checked={formData.mailTypes.includes(type)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => {
                        const arr = new Set(prev.mailTypes);
                        if (checked) arr.add(type);
                        else arr.delete(type);
                        const updatedForm = { ...prev, mailTypes: Array.from(arr) };

                        // If preview is open, revalidate on change
                        if (showPreview && !validateForm()) {
                          setShowPreview(false);
                        }

                        return updatedForm;
                      });
                    }}
                    disabled={type === "Breaking News"}
                  />
                }
                label={type}
              />
            ))}
          </Box>
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
            Breaking News is currently disabled and not in use.
          </Typography>
        </FormControl>

        {/* Status Section */}
        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <FormLabel>
            {formData.mailTypes.includes("Critical Event Notification") && formData.mailTypes.includes("Breaking News")
              ? "CEN & BN Status"
              : formData.mailTypes.includes("Critical Event Notification") 
                ? "CEN Status"
                : formData.mailTypes.includes("Breaking News")
                  ? "BN Status"
                  : "Status"} 
            <span style={{ color: "red" }}>*</span>
          </FormLabel>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
            {/* Filter out "Change" status for Breaking News */}
            {STATUS_OPTIONS.filter(btn => 
              !(formData.mailTypes.includes("Breaking News") && 
                !formData.mailTypes.includes("Critical Event Notification") && 
                btn.key === "Change")
            ).map((btn) => (
              <Button
                key={btn.key}
                sx={{
                  backgroundColor: activeStatus === btn.key ? btn.bg : "#ffffff",
                  color: activeStatus === btn.key ? "#ffffff" : btn.color,
                  border: `1px solid ${btn.color}`,
                  "&:hover": { backgroundColor: btn.bg, color: "#ffffff" },
                }}
                onClick={() => handleStatusChange(btn.key)}
                // Disable status change when both mail types are selected (use CEN status in that case)
                disabled={formData.mailTypes.includes("Critical Event Notification") &&
                        formData.mailTypes.includes("Breaking News") &&
                        !formData.mailTypes.includes("Critical Event Notification")}
              >
                {btn.key}
              </Button>
            ))}
          </Box>
          {formData.mailTypes.includes("Critical Event Notification") && 
          formData.mailTypes.includes("Breaking News") && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              When both CEN and BN are selected, all statuses are displayed.
            </Typography>
          )}

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={formData.isStatusUpdate}
                disabled={!canApplyUpdateTag(formData.status)}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isStatusUpdate: e.target.checked }))
                }
              />
            }
            label="Add Update suffix"
          />
        </FormControl>
      </Box>

      {/* ---------- Affected items block ---------- */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Affected Items <span style={{ color: "red" }}>*</span>
          </Typography>
          <Button 
            variant="contained"
            color="error"
            size="small"
            onClick={handleClearAllSelections}
            sx={{ minWidth: '120px' }}
          >
            Clear All
          </Button>
        </Box>

        {/* Impacted Customer Services */}
<FormControl fullWidth sx={{ mb: 3 }}>
  <FormLabel>Impacted Customer Services</FormLabel>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>

  <Select
    multiple
    value={formData.impactedCustomerServices}
    onChange={handleArraySelect("impactedCustomerServices")}
    input={<OutlinedInput label="Impacted Customer Services" />}
    renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
        {(selected as string[]).map((value) => (
          <Chip
            key={value}
            label={value}
            size="small"
            sx={{ m: 0.25 }}
          />
        ))}
      </Box>
    )}
    sx={{ flex: 1 }}
  >
    {ServicesShort.map((option) => (
      <MenuItem key={option} value={option}>
        <Checkbox checked={formData.impactedCustomerServices.includes(option)} />
        <ListItemText primary={option} />
      </MenuItem>
    ))}
  </Select>
  <Button 
    variant="outlined" 
    color="warning"
    size="small"
    onClick={handleClearSelection("impactedCustomerServices")}
    disabled={formData.impactedCustomerServices.length === 0}
  >
    Clear
  </Button>
                </Box>

</FormControl>
          {/* Affected Generations */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel>Generation(s)</FormLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Select
                  multiple
                  value={formData.affectedVehicles}
                  onChange={handleArraySelect("affectedVehicles")}
                  input={<OutlinedInput label="Generations" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                      {(selected as string[]).map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          sx={{ m: 0.25 }}
                        />
                      ))}
                    </Box>
                  )}
                  sx={{ flex: 1 }}
                >
                  {VEHICLES.map((option) => (
                    <MenuItem 
                      key={option} 
                      value={option}
                      disabled={
                        (option !== "MOD 1-4" && formData.affectedVehicles.includes("MOD 1-4")) ||
                        (option === "MOD 1-4" && formData.affectedVehicles.length > 0 && 
                        !formData.affectedVehicles.includes("MOD 1-4"))
                      }
                    >
                      <Checkbox checked={formData.affectedVehicles.includes(option)} />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
                <Button 
                  variant="outlined" 
                  color="warning"
                  size="small"
                  onClick={handleClearSelection("affectedVehicles")}
                  disabled={formData.affectedVehicles.length === 0}
                >
                  Clear
                </Button>
              </Box>
            </FormControl>


          {/* Brands (always) */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel>Brand(s)</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Select
                multiple
                value={formData.affectedBrands}
                onChange={handleArraySelect("affectedBrands")}
                input={<OutlinedInput label="Brands" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{ m: 0.25 }}
                      />
                    ))}
                  </Box>
                )}
                sx={{ flex: 1 }}
              >
                {BRANDS.map((option) => (
                  <MenuItem 
                    key={option} 
                    value={option}
                    disabled={
                      (option !== "All brands" && formData.affectedBrands.includes("All brands")) ||
                      (option === "All brands" && formData.affectedBrands.length > 0 && 
                      !formData.affectedBrands.includes("All brands"))||
                      (option !== "All brands (except Ford)" && 
                      formData.affectedBrands.includes("All brands (except Ford)")) ||
                      (option === "All brands (except Ford)" && formData.affectedBrands.length > 0 && 
                      !formData.affectedBrands.includes("All brands (except Ford)"))
                    }
                  >
                    <Checkbox checked={formData.affectedBrands.includes(option)} />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
              <Button 
                variant="outlined" 
                size="small"
                color="warning"
                onClick={handleClearSelection("affectedBrands")}
                disabled={formData.affectedBrands.length === 0}
              >
                Clear
              </Button>
            </Box>
          </FormControl>

          {/* Markets (always) */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel>Market(s)</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Select
                multiple
                value={formData.affectedMarkets}
                onChange={handleArraySelect("affectedMarkets")}
                input={<OutlinedInput label="Markets" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{ m: 0.25 }}
                      />
                    ))}
                  </Box>
                )}
                sx={{ flex: 1 }}
              >
                {MARKETS.map((option) => (
                  <MenuItem key={option} value={option}
                    disabled={
                      (option !== "All markets" && formData.affectedMarkets.includes("All markets")) ||
                      (option === "All markets" && formData.affectedMarkets.length > 0 && 
                      !formData.affectedMarkets.includes("All markets"))||
                      (option !== "All markets (except China)" && 
                      formData.affectedMarkets.includes("All markets (except China)")) ||
                      (option === "All markets (except China)" && formData.affectedMarkets.length > 0 && 
                      !formData.affectedMarkets.includes("All markets (except China)"))
                    }
                  >
                    <Checkbox checked={formData.affectedMarkets.includes(option)} />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
              <Button 
                variant="outlined" 
                size="small"
                color="warning"
                onClick={handleClearSelection("affectedMarkets")}
                disabled={formData.affectedMarkets.length === 0}
              >
                Clear
              </Button>
            </Box>
          </FormControl>
      </Box>

      {/* ---------- Additional Affected Items (collapsible) ---------- */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 0, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderBottom: showAdditionalAffected ? '1px solid #eee' : 'none',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => setShowAdditionalAffected((prev) => !prev)}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            Additional Affected Items
          </Typography>
          {showAdditionalAffected ? <ExpandLess /> : <ExpandMore />}
        </Box>
        <Collapse in={showAdditionalAffected} timeout="auto" unmountOnExit>
          <Box sx={{ p: 3 }}>
            {/* (CEN) Backend(s) */}
            {formData.mailTypes.includes("Critical Event Notification") && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <FormLabel>(CEN) Backend(s)</FormLabel>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0 }}>(not mandatory)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Select
                    multiple
                    value={formData.affectedBackends}
                    onChange={handleArraySelect("affectedBackends")}
                    input={<OutlinedInput label="Backends" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            sx={{ m: 0.25 }}
                          />
                        ))}
                      </Box>
                    )}
                    sx={{ flex: 1 }}
                  >
                    {BACKENDS.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={formData.affectedBackends.includes(option)} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))}
                  </Select>
                  <Button 
                    color="warning"
                    variant="outlined" 
                    size="small"
                    onClick={handleClearSelection("affectedBackends")}
                    disabled={formData.affectedBackends.length === 0}
                  >
                    Clear
                  </Button>
                </Box>
              </FormControl>
            )}

            {/* Modules */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel>Module(s)</FormLabel>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0 }}>
                (not mandatory)
              </Typography>
              <TextField
                placeholder="Search modules..."
                variant="outlined"
                size="small"
                sx={{ mt: 1, mb: 1 }}
                onChange={(e) => {
                  const searchValue = e.target.value.toLowerCase();
                  setModulesSearchTerm(searchValue);
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Select
                  multiple
                  value={formData.affectedModules}
                  onChange={handleArraySelect("affectedModules")}
                  input={<OutlinedInput label="Modules" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                      {(selected as string[]).map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          sx={{ m: 0.25 }}
                        />
                      ))}
                    </Box>
                  )}
                  sx={{ flex: 1 }}
                >
                  {MODULES.filter(option =>
                    modulesSearchTerm === '' || option.toLowerCase().includes(modulesSearchTerm)
                  ).map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={formData.affectedModules.includes(option)} />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
                <Button 
                  variant="outlined" 
                  color="warning"
                  size="small"
                  onClick={handleClearSelection("affectedModules")}
                  disabled={formData.affectedModules.length === 0}
                >
                  Clear
                </Button>
              </Box>
            </FormControl>

            {/* Services */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel>Service(s)</FormLabel>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0 }}>
                (not mandatory)
              </Typography>
              <TextField
                placeholder="Search services..."
                variant="outlined"
                size="small"
                value={servicesSearchTerm}
                sx={{ mt: 1, mb: 1 }}
                onChange={(e) => {
                  const searchValue = e.target.value.toLowerCase();
                  setServicesSearchTerm(searchValue);
                  if (searchValue) {
                    setExpandedCategories(SERVICES.map(s => s.parent));
                  } else {
                    setExpandedCategories([]);
                  }
                }}
              />
              <Box sx={{ 
                border: '1px solid #ccc', 
                borderRadius: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                p: 1
              }}>
                <List dense disablePadding>
                  {filteredServices.map(category => (
                    <React.Fragment key={category.parent}>
                      <ListItem 
                        sx={{ 
                          pl: 1, 
                          py: 0.5,
                          bgcolor: 'rgba(0, 0, 0, 0.03)',
                          borderRadius: '4px',
                          mb: 0.5,
                          fontWeight: 'bold',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.05)',
                            cursor: 'pointer'
                          }
                        }}
                        onClick={() => toggleExpandCategory(category.parent)}
                        dense
                        disablePadding
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Checkbox
                            edge="start"
                            checked={isCategoryFullySelected(category)}
                            indeterminate={isCategoryPartiallySelected(category)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleCategory(category);
                            }}
                          />
                          <ListItemText primary={category.parent} />
                          {expandedCategories.includes(category.parent) ? '▼' : '►'}
                        </Box>
                      </ListItem>
                      <Collapse in={expandedCategories.includes(category.parent)}>
                        <List dense disablePadding>
                          {category.children
                            .filter(child => 
                              !servicesSearchTerm || 
                              child.toLowerCase().includes(servicesSearchTerm.toLowerCase())
                            )
                            .map(child => (
                              <ListItem 
                                key={child}
                                dense
                                disablePadding
                                sx={{ pl: 4, py: 0.5 }}
                              >
                                <Checkbox
                                  edge="start"
                                  checked={isServiceSelected(child)}
                                  onChange={() => handleToggleService(child)}
                                />
                                <ListItemText primary={child} />
                              </ListItem>
                            ))}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
              {formData.affectedServices.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, maxWidth: '100%' }}>
                  {formData.affectedServices.map(service => (
                    <Chip
                      key={service}
                      label={service}
                      size="small"
                      onDelete={() => handleToggleService(service)}
                      sx={{ m: 0.25 }}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button 
                  variant="outlined" 
                  color="warning"
                  size="small"
                  onClick={handleClearSelection("affectedServices")}
                  disabled={formData.affectedServices.length === 0}
                >
                  Clear
                </Button>
              </Box>
            </FormControl>
          </Box>
        </Collapse>
      </Box>

      {/* Lead Incident + Start of Disturbance */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl sx={{ flex: 1, minWidth: 280 }}>
          <FormLabel>Lead Incident ID <span style={{ color: "red" }}>*</span></FormLabel>
          <TextField
            fullWidth
            name="leadIncidentId"
            value={formData.leadIncidentId}
            onChange={handleChange}
            placeholder="Enter lead incident ID"
            sx={{ mt: 1 }}
          />
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 280 }}>
          <FormLabel>Start of Disturbance (German time) <span style={{ color: "red" }}>*</span></FormLabel>
          <Flatpickr
            options={{ enableTime: true, time_24hr: true, dateFormat: "d.m.Y H:i", }}
            value={formData.startDisturbance}
            onChange={([date]: Date[]) => {
              const berlinTime = date.toLocaleString("sv-SE", { timeZone: "Europe/Tallinn" });
              const [y, m, dTime] = berlinTime.split("-");
              const [d, t] = [`${dTime.split(" ")[0]}`, `${berlinTime.split(" ")[1]}`];
              const formatted = `${d}.${m}.${y} ${t}`;
              setFormData(prev => ({ ...prev, startDisturbance: formatted }));
            }}
            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "16px", marginTop: "8px" }}
          />
          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
            Current German time: {berlinTime} ({gmtOffset})
          </Typography>
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 280 }}>
          <FormLabel>End of Disturbance (German time)</FormLabel>
          <Flatpickr
            options={{ 
              enableTime: true, 
              time_24hr: true, 
              dateFormat: "d.m.Y H:i",
            }}
            value={formData.endDisturbance}
            onChange={([date]: Date[]) => {
              const berlinTime = date.toLocaleString("sv-SE", { timeZone: "Europe/Tallinn" });
              const [y, m, dTime] = berlinTime.split("-");
              const [d, t] = [`${dTime.split(" ")[0]}`, `${berlinTime.split(" ")[1]}`];
              const formatted = `${d}.${m}.${y} ${t}`;
              
              // Check if this end time is valid compared to start time
              if (formData.startDisturbance) {
                const parseDateTime = (dateTimeStr: string) => {
                  const [datePart, timePart] = dateTimeStr.split(' ');
                  const [day, month, year] = datePart.split('.');
                  const [hours, minutes] = timePart.split(':');
                  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
                };
                
                const startDate = parseDateTime(formData.startDisturbance);
                
                if (date < startDate) {
                  setFormData(prev => ({ ...prev, endDisturbance: "" })); // Use setFormData instead of direct assignment
                  ShowCustomAlert("End of Disturbance cannot be earlier than Start of Disturbance");
                  return;
                }
              }
              
              setFormData(prev => ({ ...prev, endDisturbance: formatted }));
            }}
            disabled={!formData.startDisturbance} // Disable if no start time is entered
            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "16px", marginTop: "8px" }}
          />
          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
            Current German time: {berlinTime} ({gmtOffset})
          </Typography>
        </FormControl>
      </Box>

      {/* Type of Outage  */}

        <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel>Type of Outage <span style={{ color: "red" }}>*</span></FormLabel>
            <RadioGroup
              row
              name="outageType"
              value={formData.outageType}
              onChange={(e) => setFormData((p) => ({ ...p, outageType: e.target.value }))}
            >
              <FormControlLabel value="P1 - Total Outage" control={<Radio />} label="P1 - Total Outage" />
              <FormControlLabel value="P2 - Partial Outage" control={<Radio />} label="P2 - Partial Outage" />
            </RadioGroup>
          </FormControl>
        </Box>


      {/* Customer Impact + Technical Description */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <FormLabel>Customer Impact <span style={{ color: "red" }}>*</span></FormLabel>
          <TextField
            fullWidth
            name="customerImpactText"
            value={formData.customerImpactText}
            onChange={handleChange}
            multiline
            minRows={3}
            sx={{ mt: 1 }}
            placeholder="Describe the impact for customers"
          />
        </FormControl>

        <FormControl  fullWidth sx={{ mb: 3 }}>
          <FormLabel>Technical Description</FormLabel>
          <TextField
            fullWidth
            name="technicalDescription"
            value={formData.technicalDescription}
            onChange={handleChange}
            multiline
            minRows={4}
            sx={{ mt: 1 }}
            placeholder="Describe the technical details of the event"
          />
        </FormControl>

         <FormControl fullWidth>
          <FormLabel>Additional Information</FormLabel>
          <TextField
            fullWidth
            name="additionalInformation"
            value={formData.additionalInformation}
            onChange={handleChange}
            multiline
            minRows={4}
            sx={{ mt: 1 }}
            placeholder="Describe here any additional information"
          />
        </FormControl>
      </Box>

 {/* ---------- Impact and Root Cause Description Block ---------- */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
        Root Cause Description
        </Typography>
        <FormControl fullWidth sx={{ mb: 3 }} 
        >
          <FormLabel>
            Root Cause{(formData.status === "Resolved" || formData.cenStatus === "Resolved" || formData.bnStatus === "Resolved") && (
              <span style={{ color: "red" }}> *</span>
            )}
          </FormLabel>
          <TextField
            fullWidth
            name="rootCause"
            value={formData.rootCause}
            onChange={e => setFormData(prev => ({ ...prev, rootCause: e.target.value }))}
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            placeholder="Describe the root cause"
          />
        </FormControl>
        <FormControl fullWidth >
          <FormLabel>
            Fix{(formData.status === "Resolved" || formData.cenStatus === "Resolved" || formData.bnStatus === "Resolved") && (
              <span style={{ color: "red" }}> *</span>
            )}
          </FormLabel>
          <TextField
            fullWidth
            name="fix"
            value={formData.fix}
            onChange={e => setFormData(prev => ({ ...prev, fix: e.target.value }))}
            multiline
            minRows={2}
    sx={{ mt: 1 }}
            placeholder="Describe the fix"
          />
        </FormControl>
      </Box>
           {/* Show Preview Button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mb: 3 }}
        onClick={(e) => {
          e.preventDefault();
          if (validateForm()) setShowPreview((prev) => !prev);
        }}
      >
        {showPreview ? "Hide Preview" : "Show Preview"}
      </Button>

      {/* Preview Section */}
      {showPreview && (
        <Box sx={{ mb: 3, display: "flex", gap: 2, flexDirection: "column" }}>
          {formData.mailTypes.includes("Test Mail") && (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <CENPreview formData={{...formData, status: getEffectiveStatus(formData.cenStatus)}} />
              {/* Send Email Button for TEST */}
              <Button
                variant="contained"
                color="warning"
                fullWidth
                sx={{ mt: 2 }}
                onClick={async (e) => {
                  e.preventDefault();

                  // Disable the button when sending the email
                  setLoading(true);

                  try {
                    if (validateForm()) {
                      await sendEmail(
                        "TEST", 
                        "TEST - " + formData.leadIncidentId,
                        <CENPreview formData={{...formData, status: getEffectiveStatus(formData.cenStatus)}} />, csrfToken,
                        true,
                        "care-dsoc.mim@cariad.technology"
                      );
                      ShowCustomAlert("TEST Email sent successfully!");
                    }
                  } catch (error) {
                    ShowCustomAlert("Failed to send TEST email. Please try again.");
                  } finally {
                    // Re-enable the button after the request is completed
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send TEST"}
              </Button>
            </Box>
          )}
          {formData.mailTypes.includes("Critical Event Notification") && (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <CENPreview formData={{...formData, status: getEffectiveStatus(formData.cenStatus)}} />
              {/* Send Email Button for CEN - positioned right after CEN preview */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                onClick={async (e) => {
                  e.preventDefault();

                  // Disable the button when sending the email
                  setLoading(true);

                  try {
                    if (validateForm() && formData.mailTypes.includes("Critical Event Notification")) {
                      await sendEmail(
                        ["CEN", ...getAdditionalBrandLists()].join(", "),
                        "Critical Event Notification - " +
                          getEffectiveStatus(formData.cenStatus || formData.status) +
                          " - " +
                          formData.leadIncidentId,
                        <CENPreview formData={{...formData, status: getEffectiveStatus(formData.cenStatus)}} />, csrfToken,
                        true,
                        "care-dsoc.mim@cariad.technology"
                      );
                      ShowCustomAlert("CEN Email sent successfully!");
                    }
                  } catch (error) {
                    ShowCustomAlert("Failed to send CEN email. Please try again.");
                  } finally {
                    // Re-enable the button after the request is completed
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send CEN"}
              </Button>
            </Box>
          )}
          
          {formData.mailTypes.includes("Breaking News") && (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {validateBNBrands() ? (
                <>
                  <BNPreview formData={{...formData, status: getEffectiveStatus(formData.bnStatus)}} />
                  {/* Send Email Button for BN - positioned right after BN preview */}
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={async (e) => {
                      e.preventDefault();

                      // Disable the button when sending the email
                      setLoading(true);

                      try {
                        if (validateForm() && formData.mailTypes.includes("Breaking News")) {
                          await sendEmail(
                            getFilteredBNBrands().join(", "), // Only use allowed brands
                            "Breaking News - " + getEffectiveStatus(formData.bnStatus || formData.status) + " - " + formData.leadIncidentId,
                            <BNPreview formData={{...formData, status: getEffectiveStatus(formData.bnStatus)}} />, csrfToken,
                            true,
                            "care-dsoc.mim@cariad.technology"
                          );
                          ShowCustomAlert("BN Email sent successfully!");
                        }
                      } catch (error) {
                        ShowCustomAlert("Failed to send BN email. Please try again.");
                      } finally {
                        // Re-enable the button after the request is completed
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send BN"}
                  </Button>
                </>
              ) : (
                <Box sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeeba' }}>
                  <Typography color="warning.dark">
                    Breaking News preview is only available for "All brands" or when Seat/Cupra, Volkswagen, Skoda, or Ford are selected.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AnnouncementForm;
